/** Partner portal validation rules from the product checklist. */

export const LOAN_AMOUNT_MIN = 50_000;
export const LOAN_AMOUNT_MAX = 4_000_000;
export const COMPANY_BUSINESS_BRIEF_MAX_LENGTH = 2000;
export const PAT_PERCENT_MIN = -100;
export const PAT_PERCENT_MAX = 100;
export const BUSINESS_VINTAGE_YEARS_MAX = 100;
export const VAT_NUMBER_LENGTH = 15;
/** Mirrors the backend's `UBO_SHAREHOLDING_THRESHOLD` — a holding above
 * this percentage is automatically treated as a beneficial owner (UBO) and
 * requires personal KYC, matching `meets_ubo_threshold()`. */
export const UBO_SHAREHOLDING_THRESHOLD = 5;

export function meetsUboThreshold(percentage: number | string | null | undefined): boolean {
  if (percentage == null || percentage === "") return false;
  const value = Number(percentage);
  return !Number.isNaN(value) && value > UBO_SHAREHOLDING_THRESHOLD;
}

export const FINANCING_INVOICE = "invoice"; // SID / Invoice Discounting
export const FINANCING_REVENUE = "revenue"; // RBF
export const FINANCING_WORKING_CAPITAL = "working_capital";

export const FINANCING_TYPE_OPTIONS = [
  { value: FINANCING_INVOICE, label: "Invoice Discounting (SID)" },
  { value: FINANCING_REVENUE, label: "Revenue Based Financing (RBF)" },
  { value: FINANCING_WORKING_CAPITAL, label: "Working Capital Loan" },
] as const;

/** Same options as Merchant `platformList`. */
export const SALES_PLATFORM_OPTIONS = [
  { value: "amazon", label: "Amazon" },
  { value: "noon", label: "Noon" },
  { value: "talabaat", label: "Talabaat" },
  { value: "own", label: "Own website/D2C" },
  { value: "other", label: "Other" },
] as const;

const ALLOWED_FINANCING_TYPES = new Set<string>([
  FINANCING_INVOICE,
  FINANCING_REVENUE,
  FINANCING_WORKING_CAPITAL,
]);

export type LendingLineFormValues = {
  lender_name: string;
  loan_amount: string;
  facility_type: string;
  outstanding: string;
  emi: string;
  remarks: string;
};

export type FinancingFormValues = {
  type_of_financing: string;
  platform: string;
  platform_name: string;
  loan_amount_requested: string;
  company_business_brief: string;
  last_12_month_revenue: string;
  pat_percent: string;
  business_vintage_years: string;
  files_vat: string;
  vat_number: string;
  no_existing_loans: boolean;
  existing_lending_lines: LendingLineFormValues[];
};

export function emptyLendingLine(): LendingLineFormValues {
  return {
    lender_name: "",
    loan_amount: "",
    facility_type: "",
    outstanding: "",
    emi: "",
    remarks: "",
  };
}

export function lendingLineIsEmpty(line: LendingLineFormValues): boolean {
  return ![
    line.lender_name,
    line.loan_amount,
    line.facility_type,
    line.outstanding,
    line.emi,
    line.remarks,
  ].some((value) => value.trim());
}

/** Keep only a typed decimal draft: optional leading minus, one dot, limited fraction digits. */
function sanitizeSignedDecimalDraft(
  raw: string,
  opts: { allowNegative: boolean; maxFractionDigits: number },
): string {
  let value = raw.replace(opts.allowNegative ? /[^0-9.-]/g : /[^0-9.]/g, "");
  if (opts.allowNegative) {
    const negative = value.startsWith("-");
    value = (negative ? "-" : "") + value.replace(/-/g, "");
  }
  const sign = value.startsWith("-") ? "-" : "";
  let unsigned = sign ? value.slice(1) : value;
  const dot = unsigned.indexOf(".");
  if (dot === -1) {
    unsigned = unsigned.replace(/\./g, "");
  } else {
    unsigned =
      unsigned.slice(0, dot + 1) +
      unsigned
        .slice(dot + 1)
        .replace(/\./g, "")
        .slice(0, opts.maxFractionDigits);
  }
  return sign + unsigned;
}

function isIncompleteDecimalDraft(value: string): boolean {
  return value === "" || value === "-" || /^-?\d+\.$/.test(value) || value === "-.";
}

export function sanitizePatPercentInput(raw: string, previous = ""): string {
  const next = sanitizeSignedDecimalDraft(raw, {
    allowNegative: true,
    maxFractionDigits: 2,
  });
  if (isIncompleteDecimalDraft(next)) return next === "-." ? "-0." : next;
  const n = Number(next);
  if (!Number.isFinite(n) || n < PAT_PERCENT_MIN || n > PAT_PERCENT_MAX) {
    return previous;
  }
  return next;
}

export function finalizePatPercentInput(raw: string): string {
  const trimmed = raw.trim().replace(/\.$/, "");
  if (trimmed === "" || trimmed === "-") return "";
  return sanitizePatPercentInput(trimmed, "");
}

export function sanitizeVintageYearsInput(raw: string, previous = ""): string {
  const next = sanitizeSignedDecimalDraft(raw, {
    allowNegative: false,
    maxFractionDigits: 1,
  });
  if (isIncompleteDecimalDraft(next)) return next;
  const n = Number(next);
  if (!Number.isFinite(n) || n < 0 || n > BUSINESS_VINTAGE_YEARS_MAX) {
    return previous;
  }
  return next;
}

export function finalizeVintageYearsInput(raw: string): string {
  const trimmed = raw.trim().replace(/\.$/, "");
  if (trimmed === "") return "";
  return sanitizeVintageYearsInput(trimmed, "");
}

export function sanitizeLoanAmountInput(raw: string, previous = ""): string {
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return "";
  const amount = Number(digits);
  if (!Number.isFinite(amount) || amount > LOAN_AMOUNT_MAX) return previous;
  return digits;
}

export function loanAmountLimitMessage(): string {
  return `Must be ${LOAN_AMOUNT_MIN.toLocaleString()}–${LOAN_AMOUNT_MAX.toLocaleString()} AED`;
}

export function loanAmountRangeError(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "Loan amount requested is required.";
  const amount = Number(trimmed);
  if (!Number.isInteger(amount)) return "Enter a valid whole-number loan amount.";
  if (amount < LOAN_AMOUNT_MIN || amount > LOAN_AMOUNT_MAX) {
    return loanAmountLimitMessage();
  }
  return null;
}

function parseOptionalAmount(raw: string, label: string): { value: number | null; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null };
  const amount = Number(trimmed);
  if (!Number.isInteger(amount) || amount < 0) {
    return { value: null, error: `Enter a valid ${label}.` };
  }
  return { value: amount };
}

export function isSidFinancing(type: string): boolean {
  return type === FINANCING_INVOICE;
}

export function isRbfFinancing(type: string): boolean {
  return type === FINANCING_REVENUE;
}

export function validateFinancingDetails(form: FinancingFormValues): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.type_of_financing) {
    errors.type_of_financing = "Financing Type is required";
  } else if (!ALLOWED_FINANCING_TYPES.has(form.type_of_financing)) {
    errors.type_of_financing = "Select a valid financing type.";
  }

  if (isSidFinancing(form.type_of_financing) && !form.platform) {
    errors.platform = "Sales platform is required.";
  } else if (form.platform === "other" && !form.platform_name.trim()) {
    errors.platform_name = "Platform name is required when Sales Platform is Other.";
  }

  const brief = form.company_business_brief.trim();
  if (!brief) {
    errors.company_business_brief = "Brief about the company, promoter and business is required.";
  } else if (brief.length > COMPANY_BUSINESS_BRIEF_MAX_LENGTH) {
    errors.company_business_brief = `Brief about the company, promoter and business must be at most ${COMPANY_BUSINESS_BRIEF_MAX_LENGTH} characters.`;
  }

  const amountRaw = form.loan_amount_requested.trim();
  const amountError = loanAmountRangeError(amountRaw);
  if (amountError) errors.loan_amount_requested = amountError;

  const revenueRaw = form.last_12_month_revenue.trim();
  if (!revenueRaw) {
    errors.last_12_month_revenue = "Last 12 month revenue is required.";
  } else {
    const revenue = Number(revenueRaw);
    if (!Number.isInteger(revenue) || revenue < 0) {
      errors.last_12_month_revenue = "Enter a valid non-negative revenue amount.";
    }
  }

  const patRaw = form.pat_percent.trim();
  if (!patRaw) {
    errors.pat_percent = "PAT % is required.";
  } else if (!/^-?\d+(\.\d{1,2})?$/.test(patRaw)) {
    errors.pat_percent = "Enter a valid PAT % (up to 2 decimal places).";
  } else {
    const pat = Number(patRaw);
    if (pat < PAT_PERCENT_MIN || pat > PAT_PERCENT_MAX) {
      errors.pat_percent = `PAT % must be between ${PAT_PERCENT_MIN} and ${PAT_PERCENT_MAX}.`;
    }
  }

  const vintageRaw = form.business_vintage_years.trim();
  if (!vintageRaw) {
    errors.business_vintage_years = "Business vintage is required.";
  } else if (!/^\d+(\.\d)?$/.test(vintageRaw)) {
    errors.business_vintage_years = "Enter a valid vintage in years (one decimal place).";
  } else {
    const vintage = Number(vintageRaw);
    if (vintage <= 0 || vintage > BUSINESS_VINTAGE_YEARS_MAX) {
      errors.business_vintage_years = `Enter business vintage in years (greater than 0, at most ${BUSINESS_VINTAGE_YEARS_MAX}).`;
    }
  }

  if (form.files_vat !== "true" && form.files_vat !== "false") {
    errors.files_vat = "Select whether the merchant files VAT.";
  } else if (form.files_vat === "true") {
    const vatNumber = form.vat_number.trim();
    if (!vatNumber) {
      errors.vat_number = "VAT number is required when the merchant files VAT.";
    } else if (!/^\d{15}$/.test(vatNumber)) {
      errors.vat_number = `Enter a valid ${VAT_NUMBER_LENGTH}-digit VAT TRN.`;
    }
  }

  if (form.no_existing_loans) return errors;

  if (form.existing_lending_lines.every(lendingLineIsEmpty)) {
    errors.existing_lending_lines =
      "Add at least one existing lending line or mark as no existing lending lines.";
  }

  form.existing_lending_lines.forEach((line, index) => {
    if (lendingLineIsEmpty(line)) return;
    const prefix = `lending_line_${index}`;
    if (!line.lender_name.trim()) {
      errors[`${prefix}_lender_name`] = "Lender name is required.";
    }
    const loanAmount = parseOptionalAmount(line.loan_amount, "loan amount");
    if (!line.loan_amount.trim()) {
      errors[`${prefix}_loan_amount`] = "Loan amount is required.";
    } else if (loanAmount.error) {
      errors[`${prefix}_loan_amount`] = loanAmount.error;
    }
    if (!line.facility_type.trim()) {
      errors[`${prefix}_facility_type`] = "Facility type is required.";
    }
    const outstanding = parseOptionalAmount(line.outstanding, "outstanding amount");
    if (outstanding.error) errors[`${prefix}_outstanding`] = outstanding.error;
    const emi = parseOptionalAmount(line.emi, "EMI");
    if (emi.error) errors[`${prefix}_emi`] = emi.error;
  });

  return errors;
}

export type BusinessKycFormValues = {
  name: string;
  address: string;
  city: string;
  postal_code: string;
  business_type: string;
  business_segment: string;
  industry_sector: string;
  identity: string;
};

export function validateBusinessKycForm(form: BusinessKycFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = "Business name is required.";
  if (!form.city) errors.city = "City is required.";
  if (!form.postal_code.trim()) errors.postal_code = "PO Box / postal code is required.";
  if (!form.identity.trim()) errors.identity = "Trade license number is required.";
  if (!form.business_type) errors.business_type = "Business type is required.";
  if (!form.address.trim()) errors.address = "Company address is required.";
  if (!form.business_segment) errors.business_segment = "Business segment is required.";
  if (!form.industry_sector) errors.industry_sector = "Industry sector is required.";
  return errors;
}

export type ShareholderFormValues = {
  name: string;
  shareholder_type: string;
  shareholding_percentage: string;
  is_business_owner: boolean;
};

export function validateShareholderForm(
  form: ShareholderFormValues,
  opts?: { otherHoldingsTotal?: number },
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.shareholder_type) {
    errors.shareholder_type = "Shareholder type is required.";
  }
  if (!form.name.trim()) {
    errors.name = "Shareholder name is required.";
  }

  const holding = Number(form.shareholding_percentage);
  if (form.shareholding_percentage.trim() === "" || Number.isNaN(holding) || holding <= 0) {
    errors.shareholding_percentage = "Shareholding % is required and must be greater than 0.";
  } else {
    const others = opts?.otherHoldingsTotal ?? 0;
    if (others + holding > 100) {
      errors.shareholding_percentage = `Total shareholding cannot exceed 100% (current others: ${others.toFixed(2)}%).`;
    }
    if (meetsUboThreshold(holding) && !form.is_business_owner) {
      errors.is_business_owner = `Shareholders with more than ${UBO_SHAREHOLDING_THRESHOLD}% holding must be marked as beneficial owners.`;
    }
  }

  return errors;
}

export function shareholderRequiresPersonalKyc(opts: {
  is_business_owner: boolean;
  shareholding_percentage: number | null | undefined;
}): boolean {
  return Boolean(opts.is_business_owner) || meetsUboThreshold(opts.shareholding_percentage);
}

export type PersonalKycFormValues = {
  name: string;
  nationality: string;
  passport_no: string;
  is_uae_resident: string;
  identity: string;
};

export function validatePersonalKycForm(
  form: PersonalKycFormValues,
  shareholderType: "INDIVIDUAL" | "BUSINESS",
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = "Name is required.";

  if (shareholderType === "BUSINESS") {
    if (!form.identity.trim()) errors.identity = "Company ID is required.";
  } else {
    if (!form.nationality) errors.nationality = "Nationality is required.";
    if (form.is_uae_resident !== "true" && form.is_uae_resident !== "false") {
      errors.is_uae_resident = "UAE Resident selection is required.";
    }
    // Emirates ID and passport numbers are not required — identity is proven
    // by the uploaded documents (see requiredPersonalDocKeys).
  }
  return errors;
}

/** Required personal KYC document keys that must be present (uploaded or pending). */
export function requiredPersonalDocKeys(
  shareholderType: "INDIVIDUAL" | "BUSINESS",
  isUaeResident: boolean,
): string[] {
  if (shareholderType === "BUSINESS") return ["company_id"];
  const keys = ["passport"];
  if (isUaeResident) keys.push("emirates_id");
  return keys;
}

/** Labels of required checklist items that have no uploaded files. */
export function missingRequiredDocumentLabels(
  checklist: Array<{
    label: string;
    required: boolean;
    files?: Array<unknown> | null;
  }>,
): string[] {
  return checklist
    .filter((item) => item.required && !(item.files && item.files.length > 0))
    .map((item) => item.label);
}
